import { ConnectedPosition } from "@angular/cdk/overlay";
import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { StatementGranularity } from "common";
import {
  PickerMonthCell,
  PickerYearCell,
  StatementPeriodSelection,
} from "./month-year-period.model";

const MONTH_LABELS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];
const YEARS_PER_PAGE = 7;
const DEFAULT_HISTORY_YEARS = 7;

@Component({
  selector: "ifp-month-year-picker",
  templateUrl: "./month-year-picker.component.html",
  styleUrls: ["./month-year-picker.component.scss"],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthYearPickerComponent),
      multi: true,
    },
  ],
})
export class MonthYearPickerComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() accountOpenedOn?: Date;
  @Input() invalid = false;

  activeTab: StatementGranularity = "month";
  level: "year" | "month" = "year";
  open = false;
  disabled = false;
  displayText = "";
  navLabel = "";
  canNavPrev = false;
  canNavNext = false;
  yearCells: PickerYearCell[] = [];
  monthCells: PickerMonthCell[] = [];
  monthGridYear: number | null = null;

  readonly overlayPositions: ConnectedPosition[] = [
    { originX: "start", originY: "bottom", overlayX: "start", overlayY: "top", offsetY: 8 },
    { originX: "start", originY: "top", overlayX: "start", overlayY: "bottom", offsetY: -8 },
  ];

  private value: StatementPeriodSelection | null = null;
  private currentYear = 0;
  private minYear = 0;
  private lastElapsedMonthYear = 0;
  private lastElapsedMonth = 0;
  private lastElapsedYear = 0;
  private windowStart = 0;
  private onChange: (value: StatementPeriodSelection | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  ngOnInit(): void {
    this.computeEasternBoundaries();
  }

  // Entity mode swaps the target loan while the dialog is open — re-floor minYear at the new chain origin.
  // ngOnChanges runs before ngOnInit on the first pass, so let ngOnInit own the initial computation.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accountOpenedOn && !changes.accountOpenedOn.firstChange) {
      this.computeEasternBoundaries();
      if (this.open) {
        this.resetWindow();
        if (this.level === "year")
          this.buildYearCells();
        else
          this.buildMonthCells();
      }
    }
  }

  writeValue(value: StatementPeriodSelection | null): void {
    this.value = value ?? null;
    if (this.value)
      this.activeTab = this.value.granularity;
    this.displayText = this.buildDisplayText(this.value);
  }

  registerOnChange(fn: (value: StatementPeriodSelection | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  togglePanel(): void {
    if (this.disabled)
      return;
    if (this.open)
      this.close();
    else
      this.openPanel();
  }

  selectTab(tab: StatementGranularity): void {
    if (tab === this.activeTab)
      return;
    this.activeTab = tab;
    this.level = "year";
    this.monthGridYear = null;
    this.resetWindow();
    this.buildYearCells();
  }

  navigate(delta: number): void {
    if (this.level === "year") {
      this.windowStart = Math.min(Math.max(this.windowStart + delta, this.minYear), this.maxWindowStart());
      this.buildYearCells();
    } else if (this.monthGridYear != null) {
      const next = this.monthGridYear + delta;
      if (next < this.minYear || next > this.lastElapsedMonthYear)
        return;
      this.monthGridYear = next;
      this.buildMonthCells();
    }
  }

  selectYear(cell: PickerYearCell): void {
    if (cell.disabled)
      return;
    if (this.activeTab === "year") {
      this.commit({ granularity: "year", year: cell.year });
      return;
    }
    this.monthGridYear = cell.year;
    this.level = "month";
    this.buildMonthCells();
  }

  selectMonth(cell: PickerMonthCell): void {
    if (cell.disabled || this.monthGridYear == null)
      return;
    this.commit({ granularity: "month", year: this.monthGridYear, month: cell.month });
  }

  backToYearGrid(): void {
    this.level = "year";
    this.monthGridYear = null;
    this.buildYearCells();
  }

  close(): void {
    if (!this.open)
      return;
    this.open = false;
    this.onTouched();
  }

  trackByYear(_index: number, cell: PickerYearCell): number {
    return cell.year;
  }

  trackByMonth(_index: number, cell: PickerMonthCell): number {
    return cell.month;
  }

  private openPanel(): void {
    this.level = "year";
    this.monthGridYear = null;
    this.resetWindow();
    this.buildYearCells();
    this.open = true;
  }

  private commit(selection: StatementPeriodSelection): void {
    this.value = selection;
    this.displayText = this.buildDisplayText(selection);
    this.onChange(selection);
    this.onTouched();
    this.close();
  }

  private maxValidYear(): number {
    return this.activeTab === "month" ? this.lastElapsedMonthYear : this.lastElapsedYear;
  }

  private maxWindowStart(): number {
    return Math.max(this.minYear, this.currentYear - (YEARS_PER_PAGE - 1));
  }

  private resetWindow(): void {
    const anchor = this.value != null ? this.value.year : this.maxValidYear();
    let start = this.maxWindowStart();
    if (anchor < start)
      start = anchor;
    this.windowStart = Math.max(this.minYear, start);
  }

  private buildYearCells(): void {
    const max = this.maxValidYear();
    const windowEnd = Math.min(this.windowStart + YEARS_PER_PAGE - 1, this.currentYear);
    const cells: PickerYearCell[] = [];
    for (let year = this.windowStart; year <= windowEnd; year++) {
      cells.push({
        year,
        disabled: year < this.minYear || year > max,
        selected:
          this.value != null &&
          this.value.granularity === this.activeTab &&
          this.value.year === year,
      });
    }
    this.yearCells = cells;
    this.navLabel = windowEnd > this.windowStart ? `${this.windowStart} - ${windowEnd}` : `${this.windowStart}`;
    this.canNavPrev = this.windowStart > this.minYear;
    this.canNavNext = windowEnd < this.currentYear;
  }

  private buildMonthCells(): void {
    if (this.monthGridYear == null) {
      this.monthCells = [];
      return;
    }
    const maxMonth = this.monthGridYear === this.lastElapsedMonthYear ? this.lastElapsedMonth : 12;
    const cells: PickerMonthCell[] = [];
    for (let month = 1; month <= 12; month++) {
      cells.push({
        month,
        label: MONTH_LABELS[month - 1],
        disabled: month > maxMonth,
        selected:
          this.value != null &&
          this.value.granularity === "month" &&
          this.value.year === this.monthGridYear &&
          this.value.month === month,
      });
    }
    this.monthCells = cells;
    this.navLabel = `${this.monthGridYear}`;
    this.canNavPrev = this.monthGridYear > this.minYear;
    this.canNavNext = this.monthGridYear < this.lastElapsedMonthYear;
  }

  private buildDisplayText(selection: StatementPeriodSelection | null): string {
    if (selection == null)
      return "";
    if (selection.granularity === "month" && selection.month != null)
      return `${this.pad2(selection.month)}/${selection.year}`;
    return `01/${selection.year} - 12/${selection.year}`;
  }

  private pad2(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  private computeEasternBoundaries(): void {
    const now = this.getEasternParts();
    this.currentYear = now.year;

    this.lastElapsedMonthYear = now.month === 1 ? now.year - 1 : now.year;
    this.lastElapsedMonth = now.month === 1 ? 12 : now.month - 1;
    this.lastElapsedYear = now.year - 1;

    const floorYear = now.year - DEFAULT_HISTORY_YEARS;
    // Eastern year of the open date (matches the server gate); local getFullYear() can be off by one at a Jan-1 UTC boundary.
    const openedYear = this.accountOpenedOn ? this.getEasternParts(new Date(this.accountOpenedOn)).year : undefined;
    this.minYear = openedYear ?? floorYear;
  }

  // America/New_York, to match the server's Eastern snapping.
  private getEasternParts(date: Date = new Date()): { year: number; month: number } {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "numeric",
    }).formatToParts(date);
    const part = (type: string): number =>
      Number(parts.find((p) => p.type === type)?.value);
    return { year: part("year"), month: part("month") };
  }
}
